import { formatCad, imageUrl, toCents, type Product, type ProductStatus } from '@nsd/core';
import { useState } from 'react';
import { ErrorBox, Loading, api, useAction, useLoad } from '../ui.tsx';

function ProductRow({ product, onSaved }: { product: Product; onSaved: (p: Product) => void }) {
  const [price, setPrice] = useState((product.priceCents / 100).toFixed(2));
  const [retail, setRetail] = useState(product.compareAtCents ? (product.compareAtCents / 100).toFixed(2) : '');
  const [status, setStatus] = useState<ProductStatus>(product.status);
  const [stock, setStock] = useState(product.trackStock ? String(product.stock ?? 0) : '');
  const action = useAction();
  const dirty =
    toCents(price || '0') !== product.priceCents ||
    (retail ? toCents(retail) : null) !== (product.compareAtCents ?? null) ||
    status !== product.status;

  const save = () =>
    action.run(async () => {
      const r = await api.updateProduct(product.id, { priceCents: toCents(price), compareAtCents: retail ? toCents(retail) : null, status });
      onSaved(r.product);
    });
  const saveStock = () =>
    action.run(async () => {
      const r = stock === '' ? await api.setStock(product.id, { mode: 'untrack' }) : await api.setStock(product.id, { mode: 'set', value: Math.floor(Number(stock)), reason: 'restock' });
      onSaved(r.product);
    });
  const img = product.images[0];

  return (
    <li className="prow">
      {img ? <img src={imageUrl(img.src, 400)} alt="" width="56" height="56" /> : <span className="prow__noimg" />}
      <div className="prow__name">
        <strong>{product.name}</strong>
        <small className="muted">{product.sku} · {formatCad(product.priceCents)}</small>
      </div>
      <label>
        Price $
        <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
      </label>
      <label>
        Retail $ (strike-through)
        <input inputMode="decimal" placeholder="none" value={retail} onChange={(e) => setRetail(e.target.value)} />
      </label>
      <label>
        Status
        <select value={status} onChange={(e) => setStatus(e.target.value as ProductStatus)}>
          <option value="active">Active</option>
          <option value="hidden">Hidden</option>
          <option value="archived">Archived</option>
        </select>
      </label>
      <button className="btn" disabled={!dirty || action.busy} onClick={() => void save()}>Save</button>
      <label>
        Stock (blank = not tracked)
        <input inputMode="numeric" value={stock} onChange={(e) => setStock(e.target.value.replace(/[^0-9]/g, ''))} />
      </label>
      <button className="btn btn--ghost" disabled={action.busy || stock === (product.trackStock ? String(product.stock ?? 0) : '')} onClick={() => void saveStock()}>
        Update stock
      </button>
      {action.error && <div className="prow__err"><ErrorBox error={action.error} /></div>}
    </li>
  );
}

export function Products() {
  const { data, error, loading, setData } = useLoad(() => api.products());
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} />;
  return (
    <>
      <h1>Products</h1>
      <p className="muted">Price and stock changes apply to checkout immediately. The storefront pages show them after the next site build.</p>
      <ul className="plist">
        {data.map((p) => (
          <ProductRow key={p.id} product={p} onSaved={(np) => setData(data.map((x) => (x.id === np.id ? np : x)))} />
        ))}
      </ul>
    </>
  );
}
